import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RedirectCommand,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { RouteParameter } from '@biletix/core/common/constants/route-parameter.const';
import { isTransientHttpError } from '@biletix/core/common/helper/http-error-classifier';
import { ArtistDetail } from '@biletix/core/models/event/artist-detail.model';
import { EventService } from '@biletix/core/services/event.service';
import { GlobalErrorHandler } from '@biletix/core/services/global-error-handler.service';
import { RouterService } from '@biletix/core/services/router.service';
import { StatusCodeResponseService } from '@biletix/core/services/status-code-response.service';

/**
 * Sanatçı sayfasının yayında olup olmadığını kontrol eder; yayında değilse
 * tüm sanatçılar listesine 301 verir.
 *
 * Yayında olmama iki şekilde olur:
 *  - `active = false`: sanatçı kaydı pasife alınmış.
 *  - `showEventPage = false`: kayıt aktif ama sanatçı sayfası yayınlanmamış
 *    (içerik/telif onayı beklenen sanatçılar bu durumdadır ve çoğunluktadır).
 *
 * SEO: bu URL'ler kalıcı olarak içeriksizdir, ama sanatçı listesi aynı arama
 * niyetini karşılar. 301 ile birikmiş sıralama değeri oraya taşınır ve soft-404
 * (200 dönen boş sayfa) durumu ortadan kalkar.
 */
@Injectable()
export class ArtistDetailResolver implements Resolve<ArtistDetail | null> {

  constructor(private eventService: EventService,
              private globalErrorHandler: GlobalErrorHandler,
              private routerService: RouterService,
              private statusCodeResponseService: StatusCodeResponseService,
              private router: Router,
              @Inject(PLATFORM_ID) private platformId: Object) {
  }

  resolve(route: ActivatedRouteSnapshot,
          state: RouterStateSnapshot): Observable<ArtistDetail | RedirectCommand | null> {
    const artistCode: string = route.params[RouteParameter.PARAMS.ARTIST_CODE];

    return this.eventService.getArtistDetail(artistCode)
      .pipe(
        take(1),
        map(artistDetail => {
          // Buraya yalnızca HTTP 2xx yanıtlar ulaşır; 5xx/timeout HttpClient
          // tarafından throw edilip catchError'a düşer. Dolayısıyla veri yokluğu
          // veya pasiflik burada KALICI kabul edilir.
          if (!artistDetail) {
            return this.redirectToArtistListPermanently();
          }

          if (!this.isArtistPagePublished(artistDetail)) {
            return this.redirectToArtistListPermanently();
          }

          return artistDetail;
        }),
        catchError(err => {
          this.globalErrorHandler.handleError(err);

          // Anlık teknik hatalarda (5xx / timeout / network) 301 dönülürse
          // yayında olan bir sanatçı sayfası kalıcı taşınmış sayılıp index'ten
          // düşer. 503 ile URL index'te kalır, arama motoru sonra tekrar dener.
          if (isTransientHttpError(err)) {
            this.statusCodeResponseService.setServiceUnavailable();
            return of(null);
          }

          return of(this.redirectToArtistListPermanently());
        })
      );
  }

  /**
   * `showEventPage` servis tarafında henüz her ortamda dönmüyor. `undefined`
   * geldiğinde yönlendirme YAPILMAZ: aksi halde alanın eksik olduğu bir ortamda
   * tüm sanatçı sayfaları listeye 301 verir. Yalnızca alan açıkça `false` ise
   * sayfa yayında değil sayılır.
   */
  private isArtistPagePublished(artistDetail: ArtistDetail): boolean {
    if (artistDetail.active === false) {
      return false;
    }

    return artistDetail.showEventPage !== false;
  }

  /**
   * Sunucuda `RedirectCommand` DÖNMEK ZORUNLUDUR, `EMPTY` yeterli değildir:
   * EMPTY navigasyonu iptal eder, @angular/ssr bunu `hasNavigationError` olarak
   * görüp `null` döner ve Express kendi 404'ünü yayınlar — set edilen 301 hiç
   * kullanılmaz. Gerçek bir yönlendirmede ise engine RESPONSE_INIT'teki durum
   * kodunu Location header'ı ile birlikte yayınlar.
   */
  private redirectToArtistListPermanently(): RedirectCommand | null {
    const url = this.routerService.getAllArtistsPageUrl();
    const routerPath = this.routerService.toRouterPath(url);

    if (isPlatformBrowser(this.platformId)) {
      this.router.navigateByUrl(routerPath);
      return null;
    }

    this.statusCodeResponseService.setPermanentRedirect(url);
    return new RedirectCommand(this.router.parseUrl(routerPath));
  }
}
